/**
 * PropAtlas — Market Insights
 * Aggregates top Australian property, finance, tax, and SMSF news.
 * Refreshes from RSS feeds every 4 hours. Supports article bookmarking.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { newsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { id: 'property', label: 'Property', icon: '🏠', desc: 'Market news, price movements, listings' },
  { id: 'finance',  label: 'Finance',  icon: '📈', desc: 'Interest rates, lending, economy' },
  { id: 'tax',      label: 'Tax',      icon: '🧾', desc: 'ATO updates, property tax, CGT' },
  { id: 'smsf',     label: 'SMSF',     icon: '🏦', desc: 'Self-managed super, retirement' },
];

export default function MarketInsightsPage() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('property');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [savedTab, setSavedTab] = useState(false);
  const [savedArticles, setSavedArticles] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedUrls, setSavedUrls] = useState(new Set());
  const [error, setError] = useState('');

  const fetchNews = useCallback(async (category) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await newsAPI.getNews(category);
      setArticles(data.articles || []);
      setSavedUrls(prev => {
        // merge — keep what we know about saved state
        const next = new Set(prev);
        (data.articles || []).forEach(a => {
          if (a.is_saved) next.add(a.url);
        });
        return next;
      });
    } catch {
      setError('Could not load news. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const { data } = await newsAPI.listSaved();
      setSavedArticles(data || []);
      setSavedUrls(new Set((data || []).map(a => a.url)));
    } catch {
      // ignore
    } finally {
      setSavedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (savedTab) {
      fetchSaved();
    } else {
      fetchNews(activeTab);
    }
  }, [activeTab, savedTab, fetchNews, fetchSaved]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Forcing an upstream RSS refresh is admin-only (expensive). Other users
      // simply re-pull the latest cached articles, which the server refreshes every 4h.
      if (isAdmin) {
        await newsAPI.forceRefresh();
      }
      await fetchNews(activeTab);
    } catch {
      // ignore refresh errors
    } finally {
      setRefreshing(false);
    }
  };

  const toggleSave = async (article) => {
    const isSaved = savedUrls.has(article.url);
    // Optimistic update
    setSavedUrls(prev => {
      const next = new Set(prev);
      isSaved ? next.delete(article.url) : next.add(article.url);
      return next;
    });
    try {
      if (isSaved) {
        await newsAPI.unsaveArticle(article.url);
        if (savedTab) setSavedArticles(prev => prev.filter(a => a.url !== article.url));
      } else {
        await newsAPI.saveArticle({
          url: article.url,
          title: article.title,
          description: article.description,
          source_name: article.source_name,
          category: article.category || activeTab,
          image_url: article.image_url,
          published_at: article.published_at,
        });
      }
    } catch {
      // Revert on failure
      setSavedUrls(prev => {
        const next = new Set(prev);
        isSaved ? next.add(article.url) : next.delete(article.url);
        return next;
      });
    }
  };

  const displayArticles = savedTab ? savedArticles : articles;
  const isLoading = savedTab ? savedLoading : loading;

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1000, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 className="serif" style={{ margin: 0, fontSize: 26, color: 'var(--forest)', fontWeight: 600 }}>
            Market Insights
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-4)' }}>
            Top Australian property, finance, tax and SMSF news — refreshed every 4 hours.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || savedTab}
          className="btn ghost sm"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>↻</span>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--hairline)', paddingBottom: 0 }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => { setSavedTab(false); setActiveTab(cat.id); }}
            style={{
              padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: !savedTab && activeTab === cat.id ? 600 : 400,
              color: !savedTab && activeTab === cat.id ? 'var(--forest)' : 'var(--ink-4)',
              borderBottom: !savedTab && activeTab === cat.id ? '2px solid var(--forest)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
        <button
          onClick={() => setSavedTab(true)}
          style={{
            padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: savedTab ? 600 : 400,
            color: savedTab ? 'var(--forest)' : 'var(--ink-4)',
            borderBottom: savedTab ? '2px solid var(--forest)' : '2px solid transparent',
            marginBottom: -1, marginLeft: 'auto',
          }}
        >
          🔖 Saved ({savedUrls.size})
        </button>
      </div>

      {/* Category description */}
      {!savedTab && (
        <p style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 20 }}>
          {CATEGORIES.find(c => c.id === activeTab)?.desc}
        </p>
      )}

      {/* Content */}
      {error && (
        <div style={{ padding: '10px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 20 }}>
          {error}
        </div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ height: 120, background: 'var(--hairline)', borderRadius: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
          ))}
        </div>
      ) : displayArticles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ink-4)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{savedTab ? '🔖' : '📰'}</div>
          <div style={{ fontWeight: 500 }}>
            {savedTab ? 'No saved articles yet' : 'No articles found'}
          </div>
          <div style={{ fontSize: 13, marginTop: 6 }}>
            {savedTab ? 'Bookmark articles to read them later.' : 'Try refreshing or check back soon.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {displayArticles.map((article, idx) => (
            <ArticleCard
              key={article.url || idx}
              article={article}
              isSaved={savedUrls.has(article.url)}
              onToggleSave={() => toggleSave(article)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleCard({ article, isSaved, onToggleSave }) {
  const [hovering, setHovering] = useState(false);

  const catColors = {
    property: { bg: '#e8f4e8', color: '#2d6a2d' },
    finance:  { bg: '#e8f0fc', color: '#1a56a0' },
    tax:      { bg: '#fef3cd', color: '#856404' },
    smsf:     { bg: '#f3e8ff', color: '#6b21a8' },
  };
  const cat = catColors[article.category] || { bg: 'var(--hairline)', color: 'var(--ink-3)' };

  return (
    <div
      style={{
        display: 'flex', gap: 16, padding: 18,
        background: 'var(--card)', borderRadius: 10,
        border: '1px solid var(--hairline)',
        boxShadow: hovering ? '0 4px 16px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Thumbnail */}
      {article.image_url && (
        <img
          src={article.image_url}
          alt=""
          style={{ width: 100, height: 80, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          {article.category && (
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '2px 8px', borderRadius: 20, background: cat.bg, color: cat.color }}>
              {article.category}
            </span>
          )}
          {article.source_name && (
            <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{article.source_name}</span>
          )}
          {article.published_at && (
            <span style={{ fontSize: 11, color: 'var(--ink-4)', marginLeft: 'auto' }}>
              {formatDate(article.published_at)}
            </span>
          )}
        </div>

        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', textDecoration: 'none', lineHeight: 1.4, display: 'block', marginBottom: 6 }}
        >
          {article.title}
        </a>

        {article.description && (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {article.description}
          </p>
        )}
      </div>

      {/* Bookmark button */}
      <button
        onClick={onToggleSave}
        title={isSaved ? 'Remove bookmark' : 'Save article'}
        style={{
          flexShrink: 0, alignSelf: 'flex-start',
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 18, padding: '2px 4px',
          color: isSaved ? 'var(--forest)' : 'var(--ink-4)',
          opacity: isSaved ? 1 : 0.5,
          transition: 'color 0.15s, opacity 0.15s',
        }}
      >
        {isSaved ? '🔖' : '🏷'}
      </button>
    </div>
  );
}

function formatDate(raw) {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (isNaN(d)) return raw.slice(0, 16);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}
